import { useEffect } from 'react';
import { X } from 'lucide-react';
import { palette, transition, admin } from '../../styles/tokens';

interface SlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  width?: number;
  children: React.ReactNode;
}

export function SlidePanel({ isOpen, onClose, title, subtitle, width = 480, children }: SlidePanelProps) {
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 50,
          background: 'rgba(0,0,0,0.3)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: `opacity ${transition.normal}`,
        }}
      />
      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width, zIndex: 51,
        background: palette.white,
        borderTopLeftRadius: 16,
        borderBottomLeftRadius: 16,
        boxShadow: isOpen ? '-8px 0 32px rgba(0,0,0,0.08)' : 'none',
        transform: isOpen ? 'translateX(0)' : `translateX(${width}px)`,
        transition: `transform 250ms cubic-bezier(0.4, 0, 0.2, 1)`,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${palette.g100}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: admin.header }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12.5, color: palette.g400, marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{
            width: 28, height: 28, borderRadius: 6, border: 'none',
            background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: palette.g400, transition: `color ${transition.fast}`,
          }}
            onMouseEnter={e => (e.currentTarget.style.color = palette.g600)}
            onMouseLeave={e => (e.currentTarget.style.color = palette.g400)}
          >
            <X size={18} />
          </button>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {children}
        </div>
      </div>
    </>
  );
}
